import { useCallback, useMemo } from "react"

import { CatalogItemDetailsForm } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { useMapDispatchContext } from "@l2r-front/l2r-map"
import { useSubNetworksWithWriteAccess } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useTheme } from "@l2r-front/l2r-ui"
import { useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { API_MODULE_HORIZONTAL_SIGNING } from "../../../../common/constants/modules"
import { HORIZONTAL_SIGNING_CONDITIONS, getHorizontalSigningConditionsValues } from "../../constants/horizontalSigningConditions"
import { useHorizontalSignUpdate } from "../../hooks/mutations/useHorizontalSignUpdate"
import { useHorizontalSigningProject } from "../../hooks/queries/horizontalSigning/useHorizontalSigningProject"
import { useHorizontalSign } from "../../hooks/queries/horizontalSigning/useHorizontalSigns"
import { useHorizontalSignTypes } from "../../hooks/queries/horizontalSigning/useHorizontalSignTypes"

import * as Styled from "./HorizontalSignDetailsForm.styled"

export const HorizontalSignDetailsFormPost = (props) => {

    const {
        className,
        close,
        signId,
        onMutationRunning,
        ...otherProps
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const theme = useTheme()
    const navigate = useNavigateWithSearchParams()
    const subNetworks = useSubNetworksWithWriteAccess(null, API_MODULE_HORIZONTAL_SIGNING)
    const { setMapPin } = useMapDispatchContext()

    const { data: horizontalSign, isLoading, isRefetching, isError } = useHorizontalSign(signId)
    const { data: project } = useHorizontalSigningProject()
    const { data: horizontalSignTypes, isLoading: isLoadingTypes, isError: isErrorTypes } = useHorizontalSignTypes()

    const existingTags = useMemo(() => {
        if (project?.tags) {
            return project.tags.sort()
        }

        return []
    }, [project])

    const conditions = useMemo(() => {
        return HORIZONTAL_SIGNING_CONDITIONS.filter(condition => condition > 0)
            .map(condition => ({
                value: condition,
                label: t(I18N_NAMESPACE, getHorizontalSigningConditionsValues(t, theme)[condition].label),
            }))
    }, [t, theme])

    const mutation = useHorizontalSignUpdate(signId, {}, {
        onSuccess: (data) => {
            setMapPin()
            onMutationRunning(false)
            close(false)
            navigate(`../${data.linear_location.road}/${data.uuid}`)
        },
        onError: () => {
            onMutationRunning(false)
        },
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.horizontalSignDetailsForm.snackbar.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.horizontalSignDetailsForm.snackbar.success"),
    })

    const onSubmit = useCallback((values) => {
        mutation.mutate(values)
        onMutationRunning(true)
    }, [onMutationRunning, mutation])


    if (isLoading || isRefetching || isLoadingTypes) {
        return <Styled.FormSkeleton className={className} />
    }

    if (isError || isErrorTypes) {
        return <Styled.ErrorMessage>{t(I18N_NAMESPACE, "containers.horizontalSignDetailsForm.error")}</Styled.ErrorMessage>
    }

    return <CatalogItemDetailsForm
        className={className}
        {...otherProps}
        initialValues={horizontalSign}
        catalogTypes={horizontalSignTypes}
        typeField="horizontal_sign_type"
        typeFieldLabel={t(I18N_NAMESPACE, "containers.horizontalSignDetailsForm.type")}
        conditions={conditions}
        existingTags={existingTags}
        onSubmit={onSubmit}
        onCancel={close}
        subNetworks={subNetworks}
    />
}

HorizontalSignDetailsFormPost.propTypes = {
    className: PropTypes.string,
    close: PropTypes.func.isRequired,
    signId: PropTypes.string.isRequired,
}